import React, {useEffect, useState} from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import PageLoader from '../Components/PageLoader';
import {CustomFileUpload, CustomInput, CustomSelect} from '../Components/CustomFormElements';

import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import { AlertProvider, useAlert } from '../Components/AlertContext';
import { ProductCatalogApiProvider, useProductCatalogApi } from '../Services/ProductCatalogApiProvider';

function NewProductCatalog({ canEdit }) {

    const navigate = useNavigate();
    const location = useLocation();
    const { organizationId } = useParams();
    const { showAlert } = useAlert();
    const { showConfirmation } = useConfirmation();

    const { uploadCatalog, getConnections, uploadConnectionCatalog } = useProductCatalogApi();
    const [file, setFile] = useState(null);
    const [connections, setConnections] = useState([]);
    const [loading, setLoading] = useState(false);
    const [catalogType, setCatalogType] = useState('ASSET');
    const [connectionKey, setConnectionKey] = useState();
    const [catalogName, setCatalogName] = useState();


    function onSetFiles(files) {
        if (!files || files.length === 0) {
            setFile(null);
            return;
        }

        const selectedFile = files[0];
        setFile(selectedFile);
    }

    useEffect(() => {
        getConnections().then(connections => {
            setConnections(connections ?? []);
        });
    }, [organizationId, getConnections])

    async function onUpload() {
        const catalogData = {
            catalog_type: catalogType,

        };

        if (catalogType === 'ASSET') {
            if (!file) {
                showAlert('warning', 'No file selected');
                return;
            }
        }

        if (catalogType === 'KEY_CRM') {
            if (!connectionKey) {
                showAlert('warning', 'No connection selected');
                return;
            }
            catalogData.connection_key = connectionKey;
            catalogData.catalog_name = catalogName;
        }

        setLoading(true);

        let catalog = null;

        if (catalogType === 'KEY_CRM') {
            await uploadConnectionCatalog(catalogData);
        }
        else if (catalogType === 'ASSET') {
            catalog = await uploadCatalog(file);
        }
        setLoading(false);

        if (catalog && catalog.item && catalogType === 'ASSET') {
            showAlert('success', 'File was successfully uploaded!');
            navigate(`/organizations/${organizationId}/product-catalogs/${catalog.item.id}`);
        }
        else if (catalogType === 'KEY_CRM') {
            showAlert('success', 'Product catalog was successfully created!');
            navigate(`/organizations/${organizationId}/product-catalogs`);
        }
    }

    if (loading) {
        return <PageLoader />;
    }

    return (
        <div className="container">
            <Breadcrumbs />
            <div className="row d-flex gap">
                <div className="form-container radius-yes p-2 bg-yes col-12 gap d-flex flex-column"  >
                    <h2 className='h2-title'>Product Catalog</h2>
                    <div id="shop-export" className="int-col-2 p-2 border-yes radius-yes">
                        <div className='input-row input-block d-flex flex-row justify-between'>
                            <div className='input-description'>
                                <div className='input-title'>Product Type</div>
                                <div className='small-font'>Select product type</div>
                            </div>
                            <div className='align-wrapper d-flex align-center'>
                                <CustomSelect
                                    id='product-type'
                                    label="Product Type"
                                    options={[
                                        { value: 'ASSET', label: 'Asset' },
                                        { value: 'KEY_CRM', label: 'Key Crm' }
                                    ]}
                                    value={catalogType}
                                    onChange={(value) => {
                                        setCatalogType(value);
                                    }}
                                    multiSelect={false}
                                    disabled={!canEdit}
                                />
                            </div>
                        </div>
                        {catalogType === 'ASSET' && (
                          <div className='input-block d-flex flex-row justify-between'>
                              <div className='input-description'>
                                  <div className='input-title'>Upload file</div>
                                  <div className='small-font'>Add the file with the product catalog</div>
                              </div>
                              <div className='align-wrapper d-flex align-center'>
                                  <AlertProvider>
                                      <ConfirmationProvider>
                                          <CustomFileUpload
                                            multiple={false}
                                            maxFileSize={20 * 1024 * 1024} // 2 MB
                                            allowedFormats={['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel', 'text/csv']}
                                            showUploadedFiles={true}
                                            allowFileDeletion={true}
                                            showAlert={showAlert}
                                            showConfirmation={showConfirmation}
                                            onFileSelect={(files) => onSetFiles(files)} // Capture selected files
                                          />
                                      </ConfirmationProvider>
                                  </AlertProvider>
                              </div>
                          </div>
                        )}
                        {catalogType === 'KEY_CRM' && (
                          <>
                              <div className="input-block d-flex flex-row justify-between">
                                  <div className="input-description">
                                      <div className="input-title">Catalog Name</div>
                                      <div className="small-font">Enter catalog name</div>
                                  </div>
                                  <div className="align-wrapper d-flex align-center">
                                      <CustomInput
                                        id="catalog-name"
                                        label="Catalog Name"
                                        placeholder="Enter catalog name"
                                        icon={<i className="fa-solid fa-comments"></i>}
                                        value={catalogName}
                                        onChange={(id, value) => setCatalogName(value)}
                                        disabled={!canEdit}
                                        type="text"
                                      />
                                  </div>
                              </div>

                              <div className="input-block d-flex flex-row justify-between">
                                  <div className="input-description">
                                      <div className="input-title">Connection</div>
                                      <div className="small-font">Select connection</div>
                                  </div>
                                  <div className="align-wrapper d-flex align-center">
                                      <CustomSelect
                                        id="connection-select"
                                        label="Connection"
                                        options={(connections || []).map(conn => ({value: conn.id, label: conn.name}))}
                                        value={connectionKey}
                                        multiSelect={false}
                                        onChange={val => setConnectionKey(val)}
                                      />
                                  </div>
                              </div>
                          </>
                        )}
                        {canEdit && (
                            <Button
                                variant="primary"
                                size="medium"
                                onClick={() => navigate(`/organizations/${organizationId}/product-catalogs`)}
                            >
                                Cancel
                            </Button>
                        )}
                        {canEdit && (
                            <Button variant="primary" size="medium" onClick={onUpload}>
                                Process catalog
                            </Button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function NewProductCatalogWrapper({ canEdit }) {
  return (
    <AlertProvider>
      <ConfirmationProvider>
        <ProductCatalogApiProvider>
          <NewProductCatalog canEdit={canEdit} />
        </ProductCatalogApiProvider>
      </ConfirmationProvider>
    </AlertProvider>
  );
}

export default NewProductCatalogWrapper;
